import React, {FC, useEffect, useMemo, useState} from 'react'
import {toast} from 'react-toastify'
import type {PmsJobRecord} from '../../core/pms/_models'
import {applyJobMasking, getMaskableOverdueJobs, unmaskAllJobs} from '../../core/pms/_requests'
import {formatJobDate, getDisplayedJobState, isMaskedJob} from './pmsJobHelpers'

interface Props {
  visible: boolean
  vesselId?: number | null
  vesselName?: string | null
  onClose: () => void
  onSuccess?: () => void
}

const MODAL_STYLE: React.CSSProperties = {
  background: 'rgba(2, 6, 23, 0.68)',
  position: 'fixed',
  inset: 0,
  zIndex: 2100,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '24px',
  overflow: 'auto',
}

export const JobMaskingModal: FC<Props> = ({visible, vesselId, vesselName, onClose, onSuccess}) => {
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [jobs, setJobs] = useState<PmsJobRecord[]>([])
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())

  const eligibleIds = useMemo(
    () => jobs.map((job) => job.id).filter((id): id is number => typeof id === 'number' && id > 0),
    [jobs]
  )
  const selectedCount = selectedIds.size
  const totalCount = jobs.length
  const maskedCount = useMemo(() => jobs.filter((job) => isMaskedJob(job)).length, [jobs])
  const unmaskedCount = totalCount - maskedCount
  const allSelected = eligibleIds.length > 0 && selectedCount === eligibleIds.length
  const hasVessel = Boolean(vesselId)
  const isBusy = loading || saving

  const selectedSummary = useMemo(() => {
    if (!selectedCount) return 'No jobs selected'
    return `${selectedCount} selected`
  }, [selectedCount])

  const loadJobs = async () => {
    if (!visible) return
    if (!vesselId) {
      setJobs([])
      setSelectedIds(new Set())
      return
    }
    setLoading(true)
    try {
      const data = await getMaskableOverdueJobs(vesselId)
      setJobs(data)
      setSelectedIds(new Set())
    } catch (error: any) {
      console.error('Error loading maskable overdue jobs:', error)
      toast.error('Failed to load overdue jobs', {position: 'top-center'})
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void loadJobs()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, vesselId])

  const toggleSelected = (jobId?: number | null) => {
    if (!jobId) return
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(jobId)) {
        next.delete(jobId)
      } else {
        next.add(jobId)
      }
      return next
    })
  }

  const selectAll = () => {
    if (allSelected) {
      setSelectedIds(new Set())
      return
    }
    setSelectedIds(new Set(eligibleIds))
  }

  const applyMask = async (masked: boolean) => {
    if (!selectedCount) {
      toast.info('Select at least one job first.', {position: 'top-center'})
      return
    }
    const jobIds = Array.from(selectedIds)
    setSaving(true)
    try {
      const result = await applyJobMasking({jobIds, masked})
      toast.success(
        masked
          ? `Masked ${result.updated} job(s) successfully`
          : `Unmasked ${result.updated} job(s) successfully`,
        {position: 'top-center'}
      )
      await loadJobs()
      onSuccess?.()
    } catch (error: any) {
      console.error('Error saving job masking:', error)
      toast.error('Failed to update job masking', {position: 'top-center'})
    } finally {
      setSaving(false)
    }
  }

  const handleUnmaskAll = async () => {
    if (!vesselId) {
      toast.error('Select a vessel first.', {position: 'top-center'})
      return
    }
    setSaving(true)
    try {
      const result = await unmaskAllJobs(vesselId)
      toast.success(`Unmasked ${result.updated} job(s) successfully`, {position: 'top-center'})
      await loadJobs()
      onSuccess?.()
    } catch (error: any) {
      console.error('Error unmasking all jobs:', error)
      toast.error('Failed to unmask jobs', {position: 'top-center'})
    } finally {
      setSaving(false)
    }
  }

  const emptyMessage = !hasVessel
    ? 'Select a vessel before opening masking.'
    : 'There are no overdue jobs available for masking.'

  if (!visible) return null

  return (
    <div
      style={MODAL_STYLE}
      onClick={(e) => e.target === e.currentTarget && onClose()}
      role='presentation'
    >
      <div
        className='bg-white rounded-4 shadow-lg d-flex flex-column overflow-hidden position-relative'
        style={{width: 'min(1280px, 96vw)', maxHeight: '92vh'}}
        onClick={(e) => e.stopPropagation()}
      >
        {saving && (
          <div
            className='position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center'
            style={{background: 'rgba(248, 250, 252, 0.7)', backdropFilter: 'blur(2px)', zIndex: 5}}
          >
            <div className='text-center bg-white rounded-4 shadow px-5 py-4 border'>
              <div className='spinner-border text-primary mb-3' role='status'>
                <span className='visually-hidden'>Saving</span>
              </div>
              <div className='fw-semibold text-dark'>Updating job masking...</div>
              <div className='text-muted fs-7 mt-1'>
                Please wait while the audit view is refreshed.
              </div>
            </div>
          </div>
        )}

        <div className='d-flex align-items-start justify-content-between border-bottom px-4 py-4 bg-white'>
          <div className='pe-3'>
            <div className='d-flex align-items-center gap-2 mb-2'>
              <span className='badge badge-light-danger px-3 py-2'>PMS Job Masking</span>
              <span className='badge badge-light-primary px-3 py-2'>Audit Workflow</span>
            </div>
            <h5 className='modal-title text-dark m-0 fs-2 fw-bold'>Job Masking</h5>
            <div className='text-muted fs-7 mt-2'>
              {vesselName ? `Audit view for ${vesselName}` : 'Select a vessel to manage masking'}
            </div>
          </div>

          <div className='d-flex align-items-center gap-2 flex-wrap justify-content-end'>
            <button
              type='button'
              className='btn btn-light btn-sm'
              onClick={loadJobs}
              disabled={isBusy || !hasVessel}
            >
              Refresh
            </button>
            <button type='button' className='btn-close ms-2' onClick={onClose} disabled={saving} />
          </div>
        </div>

        <div className='px-4 py-3 border-bottom bg-light'>
          <div className='d-flex flex-wrap gap-2 align-items-center justify-content-between'>
            <div className='d-flex flex-wrap gap-2 align-items-center'>
              <span className='badge badge-light-primary px-3 py-2'>Overdue: {totalCount}</span>
              <span className='badge badge-light-warning px-3 py-2'>Unmasked: {unmaskedCount}</span>
              <span className='badge badge-light-success px-3 py-2'>Masked: {maskedCount}</span>
              <span className='badge badge-light-dark px-3 py-2'>{selectedSummary}</span>
            </div>
            <div className='d-flex flex-wrap gap-2'>
              <button
                type='button'
                className='btn btn-sm btn-danger'
                onClick={() => void applyMask(true)}
                disabled={isBusy || selectedCount === 0}
              >
                Mask selected
                <span className='badge bg-white text-danger ms-2'>{selectedCount}</span>
              </button>
              <button
                type='button'
                className='btn btn-sm btn-warning'
                onClick={() => void applyMask(false)}
                disabled={isBusy || selectedCount === 0}
              >
                Unmask selected
                <span className='badge bg-white text-warning ms-2'>{selectedCount}</span>
              </button>
              <button
                type='button'
                className='btn btn-sm btn-outline-warning'
                onClick={() => void handleUnmaskAll()}
                disabled={isBusy || !hasVessel || totalCount === 0}
              >
                Unmask All
              </button>
            </div>
          </div>
          <div className='text-muted fs-7 mt-2'>
            Select overdue jobs and mask them for audit display. Masked jobs render as scheduled in
            downstream views.
          </div>
        </div>

        <div className='flex-grow-1 overflow-auto p-4 bg-white'>
          {loading ? (
            <div className='d-flex justify-content-center align-items-center py-12'>
              <div className='text-center'>
                <div className='spinner-border text-primary' role='status'>
                  <span className='visually-hidden'>Loading</span>
                </div>
                <div className='text-muted mt-3'>Loading overdue jobs...</div>
              </div>
            </div>
          ) : !hasVessel ? (
            <div className='text-center text-muted py-12'>{emptyMessage}</div>
          ) : jobs.length === 0 ? (
            <div className='d-flex align-items-center justify-content-center py-12'>
              <div className='text-center' style={{maxWidth: 460}}>
                <div className='badge badge-light-success px-3 py-2 mb-3'>No action required</div>
                <h5 className='text-dark fw-bold mb-2'>
                  There are no overdue jobs available for masking.
                </h5>
                <div className='text-muted'>
                  Once a vessel has overdue jobs, they will appear here for audit masking and
                  unmasking.
                </div>
              </div>
            </div>
          ) : (
            <div className='d-flex flex-column gap-3'>
              <div className='d-flex flex-wrap gap-2 align-items-center justify-content-between'>
                <div className='text-muted fs-7'>
                  {selectedCount > 0
                    ? `${selectedCount} overdue job${selectedCount === 1 ? '' : 's'} selected`
                    : 'Use the checkboxes to build a masking set.'}
                </div>
                <div className='d-flex flex-wrap gap-2'>
                  <span className='badge badge-light-danger px-3 py-2'>Overdue / unmasked</span>
                  <span className='badge badge-light-success px-3 py-2'>Masked</span>
                </div>
              </div>

              <div className='border rounded-4 overflow-hidden'>
                <div className='table-responsive' style={{maxHeight: '58vh', overflow: 'auto'}}>
                  <table className='table table-hover align-middle mb-0'>
                    <thead
                      className='bg-white'
                      style={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 2,
                        boxShadow: '0 1px 0 rgba(15, 23, 42, 0.08)',
                      }}
                    >
                      <tr className='text-uppercase text-muted fw-semibold fs-8'>
                        <th className='text-center' style={{width: 56}}>
                          <input
                            type='checkbox'
                            className='form-check-input'
                            checked={allSelected}
                            onChange={selectAll}
                            disabled={eligibleIds.length === 0}
                          />
                        </th>
                        <th>Job Code</th>
                        <th>Title</th>
                        <th>Vessel</th>
                        <th>Due Date</th>
                        <th>State</th>
                        <th>Mask Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {jobs.map((job, index) => {
                        const jobId = job.id ?? 0
                        const masked = isMaskedJob(job)
                        const toneClass = masked ? 'bg-light-success' : 'bg-light-danger'
                        const rowBorder = selectedIds.has(jobId)
                          ? 'border-start border-3 border-primary'
                          : ''
                        return (
                          <tr
                            key={jobId || `${job.jobCode || 'job'}-${index}`}
                            className={`${toneClass} ${rowBorder}`.trim()}
                          >
                            <td className='text-center'>
                              <input
                                type='checkbox'
                                className='form-check-input'
                                checked={selectedIds.has(jobId)}
                                onChange={() => toggleSelected(job.id)}
                                disabled={!job.id || saving}
                              />
                            </td>
                            <td className='fw-semibold text-dark'>{job.jobCode || '-'}</td>
                            <td>
                              <div className='fw-semibold text-dark'>
                                {job.title || job.jobDescription || '-'}
                              </div>
                              <div className='text-muted fs-8'>
                                {masked ? 'Scheduled maintenance job' : 'Overdue maintenance job'}
                              </div>
                            </td>
                            <td>{job.vesselName || '-'}</td>
                            <td>
                              <div className='fw-semibold text-dark'>
                                {formatJobDate(job.dueDate)}
                              </div>
                            </td>
                            <td>
                              <span
                                className={`badge ${
                                  masked ? 'badge-light-success' : 'badge-light-danger'
                                }`}
                              >
                                {getDisplayedJobState(job)}
                              </span>
                            </td>
                            <td>
                              <span
                                className={`badge ${
                                  masked ? 'badge-light-success' : 'badge-light-danger'
                                }`}
                              >
                                {masked ? 'Masked' : 'Unmasked'}
                              </span>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className='border-top px-4 py-3 bg-white d-flex align-items-center justify-content-between gap-3 flex-wrap'>
          <div className='text-muted fs-7'>
            {selectedCount > 0
              ? `${selectedCount} job${
                  selectedCount === 1 ? '' : 's'
                } ready for audit masking changes.`
              : 'No jobs selected.'}
          </div>
          <div className='d-flex gap-2'>
            <button type='button' className='btn btn-light' onClick={onClose} disabled={saving}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
